import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/of';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/mergeMap';
import type { DeleteCardInput } from '@atlassian/jira-relay/src/__generated__/deleteCardMutation.graphql';
import type { DeleteCardExtraInput } from '../../rest/card/delete/index.tsx';
import { deleteCardService } from '../../services/card/delete/index.tsx';
import { makeServiceContext } from '../../services/service-context.tsx';
import type { Issue } from '../../model/issue/issue-types.tsx';
import {
	ISSUE_DELETE_REQUEST,
	issueDeleteFailure,
	type IssueDeleteRequestAction,
	issueDeleteSuccess,
} from '../../state/actions/issue/delete/index.tsx';
import {
	activationIdSelector,
	cloudIdSelector,
	rapidViewIdSelector,
} from '../../state/selectors/software/software-selectors.tsx';
import type { Action, ActionsObservable, MiddlewareAPI, State } from '../../state/types.tsx';

export const deleteCardInputBuilder = (
	state: State,
	action: IssueDeleteRequestAction,
): DeleteCardInput => {
	const siteId = cloudIdSelector(state);
	const activationId = activationIdSelector(state);
	const boardId = rapidViewIdSelector(state);
	const cardId = action.payload.issue.id.toString();

	// TODO: Update this to use ARI library @atlassian/ari when dependency is accepted https://jdog.jira-dev.com/browse/TOTEM-3113
	const cardAri = `ari:cloud:jira-software:${siteId}:card/activation/${activationId}/${boardId}/${cardId}`;

	return {
		cardId: cardAri,
	};
};

export const extraInputBuilder = (
	state: State,
	action: IssueDeleteRequestAction,
): DeleteCardExtraInput => ({
	cloudId: cloudIdSelector(state),
	issueId: action.payload.issue.id,
});

export const handleError = ({
	error,
	issue,
	optimisticId,
}: {
	error: Error;
	issue: Issue;
	optimisticId: string;
}): Action => {
	try {
		const response: unknown = JSON.parse(error.message);
		if (
			response &&
			typeof response === 'object' &&
			'errorMessages' in response &&
			response.errorMessages &&
			Array.isArray(response.errorMessages)
		) {
			const errors = response.errorMessages.map((message) => String(message));
			return issueDeleteFailure(optimisticId, issue, errors);
		}
	} catch (_parseErr) {
		/* empty */
	}
	return issueDeleteFailure(optimisticId, issue);
};

const cardDeleteEpic = (action$: ActionsObservable, store: MiddlewareAPI): Observable<Action> =>
	action$.ofType(ISSUE_DELETE_REQUEST).mergeMap((action: IssueDeleteRequestAction) => {
		const {
			payload: { issue },
			meta: {
				optimistic: { id: optimisticId },
			},
		} = action;

		const state = store.getState();
		const ctx = makeServiceContext(state);
		const deleteCardInput = deleteCardInputBuilder(state, action);
		const extraInput = extraInputBuilder(state, action);

		return deleteCardService(ctx, { deleteCardInput, extraInput })
			.flatMap(() => Observable.of(issueDeleteSuccess(optimisticId, issue.id)))
			.catch((err) => Observable.of(handleError({ error: err, optimisticId, issue })));
	});

export default cardDeleteEpic;
